/**
 * SizilPro — useBuilderImageUpload
 * Uploads an image file to the proposal-assets Supabase Storage bucket
 * and returns the public URL.
 *
 * Bucket:  proposal-assets  (already exists)
 * Path:    {clientId}/builder-images/{timestamp}-{safeFilename}
 */

import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";

interface UploadResult {
  publicUrl: string;
}

interface UploadError {
  message: string;
}

function safeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .replace(/-{2,}/g, "-")
    .slice(0, 80);
}

export function useBuilderImageUpload() {
  const clientId = useAuthStore((s) => s.clientId);

  return useMutation<UploadResult, UploadError, File>({
    mutationFn: async (file: File) => {
      if (!clientId) {
        throw { message: "No client session found. Please reload and try again." };
      }

      const filePath = `${clientId}/builder-images/${Date.now()}-${safeName(file.name)}`;

      const { error: uploadError } = await supabase.storage
        .from("proposal-assets")
        .upload(filePath, file, { upsert: false, contentType: file.type });

      if (uploadError) {
        throw { message: uploadError.message };
      }

      const { data: urlData } = supabase.storage
        .from("proposal-assets")
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw { message: "Upload succeeded but could not retrieve public URL." };
      }

      return { publicUrl: urlData.publicUrl };
    },
  });
}
